import React, { useEffect, useState } from "react";
import Tooltip from "../shared/Tooltip";
import ExportTriggerButton from "../shared/ExportTriggerButton";
import { reportingGoals } from "../../src/reportingGoals";

const formatNumber = (value) => {
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return "0";
  }

  return numericValue.toLocaleString(undefined);
};

const trackedValue = (value, formatted) =>
  value === undefined || value === null ? "—" : formatted;

const formatPercent = (value) => {
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return "0.00";
  }

  return numericValue.toFixed(2);
};

const changeBadge = (change) => {
  const value = change == null ? null : Number(change);
  const label = value == null ? "—" : `${Math.abs(Math.round(value))}%`;
  const isZero = value == null || value === 0;
  const style = {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: 500,
    marginTop: "4px",
    background: isZero ? "#f3f4f6" : value > 0 ? "#dcfce7" : "#fee2e2",
    color: isZero ? "#374151" : value > 0 ? "#166534" : "#991b1b",
  };

  const arrow =
    value > 0 ? (
      <svg
        style={{
          width: "12px",
          height: "12px",
          marginRight: "4px",
          transform: "rotate(-45deg)",
        }}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
          clipRule="evenodd"
        />
      </svg>
    ) : value < 0 ? (
      <svg
        style={{
          width: "12px",
          height: "12px",
          marginRight: "4px",
          transform: "rotate(45deg)",
        }}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
          clipRule="evenodd"
        />
      </svg>
    ) : null;

  return (
    <span style={style}>
      {arrow}
      {label}
    </span>
  );
};

const MetricCell = ({
  children,
  change,
  comparisonAvailable,
  columnClass = "col-md-2",
}) => (
  <div
    className={`${columnClass} cta-insight-column`}
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
    }}
  >
    <span>{children}</span>
    {comparisonAvailable && changeBadge(change)}
  </div>
);

const CampaignRow = ({
  campaign,
  currency,
  comparisonAvailable,
  showWebsiteName,
  showSubmissions,
  goals,
}) => (
  <a target="_blank" rel="noopener noreferrer" href={campaign.url}>
    <div
      className="col-xs-12 light text-xl"
      style={{
        borderBottom: "1px solid #f2f2f2",
        paddingBottom: "10px",
        marginTop: "15px",
      }}
    >
      <div
        className="col-md-2 cta-insight-column"
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {campaign.name}
        {showWebsiteName && campaign.website_name && (
          <div
            className="text-slate-400"
            style={{
              fontSize: "13px",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {campaign.website_name}
          </div>
        )}
      </div>

      <div className="col-md-10 no-padding-sides">
        <MetricCell
          columnClass="col-md-1"
          change={campaign.views_change}
          comparisonAvailable={comparisonAvailable}
        >
          {formatNumber(campaign.views)}
        </MetricCell>
        <MetricCell
          columnClass="col-md-2"
          change={campaign.conversions_change}
          comparisonAvailable={comparisonAvailable}
        >
          <span style={{ whiteSpace: "nowrap" }}>
            {formatNumber(campaign.conversions)} (
            {formatPercent(campaign.conversion_rate)}%)
          </span>
        </MetricCell>
        <MetricCell
          columnClass="col-md-2"
          change={
            showSubmissions
              ? campaign.submissions_change
              : campaign.subscribes_change
          }
          comparisonAvailable={comparisonAvailable}
        >
          <span style={{ whiteSpace: "nowrap" }}>
            {formatNumber(
              showSubmissions ? campaign.submissions : campaign.subscribes
            )}{" "}
            (
            {formatPercent(
              showSubmissions
                ? campaign.submission_rate
                : campaign.subscribe_rate
            )}
            %)
          </span>
        </MetricCell>
        {goals.checkouts && (
          <MetricCell
            columnClass="col-md-2"
            change={campaign.orders_change}
            comparisonAvailable={comparisonAvailable}
          >
            {trackedValue(
              campaign.orders,
              `${formatNumber(campaign.orders)} (${formatPercent(
                campaign.order_rate
              )}%)`
            )}
          </MetricCell>
        )}
        {goals.checkouts && (
          <MetricCell
            columnClass="col-md-1"
            change={campaign.revenue_change}
            comparisonAvailable={comparisonAvailable}
          >
            {trackedValue(
              campaign.revenue,
              `${currency}${formatNumber(campaign.revenue)}`
            )}
          </MetricCell>
        )}
        {goals.checkouts && (
          <MetricCell
            columnClass="col-md-1"
            change={campaign.aov_change}
            comparisonAvailable={comparisonAvailable}
          >
            {trackedValue(
              campaign.aov,
              `${currency}${formatNumber(campaign.aov)}`
            )}
          </MetricCell>
        )}
        {goals.bookings && (
          <MetricCell
            columnClass="col-md-1"
            change={campaign.bookings_change}
            comparisonAvailable={comparisonAvailable}
          >
            {trackedValue(campaign.bookings, formatNumber(campaign.bookings))}
          </MetricCell>
        )}
        {goals.custom && (
          <MetricCell
            columnClass="col-md-1"
            change={campaign.custom_goals_change}
            comparisonAvailable={comparisonAvailable}
          >
            {trackedValue(
              campaign.custom_goals,
              formatNumber(campaign.custom_goals)
            )}
          </MetricCell>
        )}
      </div>
    </div>
  </a>
);

const ColumnTitle = ({ label, tooltip, columnClass = "col-md-2" }) => (
  <div
    className={`${columnClass} cta-insight-column`}
    style={{ position: "relative" }}
  >
    <div style={{ display: "flex", alignItems: "center" }}>
      <p className="text-xl mb-0">{label}</p>
      <Tooltip tooltip={tooltip} inline={true} />
    </div>
  </div>
);

const ColumnTitles = ({ showSubmissions, goals }) => (
  <div
    className="col-xs-12"
    style={{
      borderBottom: "1px solid #f2f2f2",
      paddingBottom: "7px",
      marginTop: "15px",
    }}
  >
    <div className="col-md-2 cta-insight-column">
      <p className="text-xl">Name</p>
    </div>
    <div className="col-md-10 no-padding-sides">
      <ColumnTitle
        columnClass="col-md-1"
        label="Viewers"
        tooltip="Number of unique people who viewed this campaign"
      />
      <ColumnTitle
        columnClass="col-md-2"
        label="Conversions"
        tooltip="Number of unique people who converted on first step"
      />
      <ColumnTitle
        columnClass="col-md-2"
        label={showSubmissions ? "Submissions" : "Subscribes"}
        tooltip={
          showSubmissions
            ? "Unique viewers who submitted a form in this campaign"
            : "Unique viewers who subscribed by submitting their email or phone number"
        }
      />
      {goals.checkouts && (
        <ColumnTitle
          columnClass="col-md-2"
          label="Orders"
          tooltip="Unique conversions who purchased within the configured attribution window"
        />
      )}
      {goals.checkouts && (
        <ColumnTitle
          columnClass="col-md-1"
          label="Revenue"
          tooltip="Revenue from those who converted with a campaign and then purchased"
        />
      )}
      {goals.checkouts && (
        <ColumnTitle
          columnClass="col-md-1"
          label="AOV"
          tooltip="Average revenue of those who converted with a campaign and then purchased"
        />
      )}
      {goals.bookings && (
        <ColumnTitle
          columnClass="col-md-1"
          label="Bookings"
          tooltip="Unique visitors who booked a meeting through a scheduler element"
        />
      )}
      {goals.custom && (
        <ColumnTitle
          columnClass="col-md-1"
          label={goals.customGoalLabel}
          tooltip={`Unique visitors who completed ${goals.customGoalLabel}`}
        />
      )}
    </div>
  </div>
);

const TOP_CAMPAIGNS_ANCHOR = "top-campaigns";

const WebsiteTopCampaigns = (props) => {
  const {
    website,
    topCampaigns,
    timeframeOptions,
    selectedTimeframe,
    startDate,
    endDate,
    exportTriggerUrl,
    insightsUrl,
    overview,
    currency,
    onTimeframeChange,
    loading,
    loadError,
    onRetry,
    hideSeeAll,
    emptyStateUrl,
    showSubmissions,
    reportingGoals: reportingGoalsProp,
  } = props;
  const campaigns = topCampaigns?.campaigns || [];
  const hasCampaigns = topCampaigns?.has_campaigns;
  const goals = reportingGoals(reportingGoalsProp || website);
  const comparisonAvailable = topCampaigns?.comparison_available === true;
  const resolvedTimeframe =
    selectedTimeframe || (startDate || endDate ? "custom" : "last_30_days");
  const [selectedTimeframeState, setSelectedTimeframeState] =
    useState(resolvedTimeframe);

  useEffect(() => {
    setSelectedTimeframeState(resolvedTimeframe);
  }, [resolvedTimeframe]);

  const buildInsightsUrl = (timeframe, customStartDate, customEndDate) => {
    const url = new URL(insightsUrl, window.location.origin);
    if (!overview) {
      url.searchParams.set("insight", "campaigns");
    }
    url.searchParams.set("timeframe", timeframe);
    if (timeframe === "custom") {
      if (customStartDate) {
        url.searchParams.set("start_date", customStartDate);
      }
      if (customEndDate) {
        url.searchParams.set("end_date", customEndDate);
      }
    }
    url.hash = TOP_CAMPAIGNS_ANCHOR;
    return url.toString();
  };

  const navigateToTimeframe = (timeframe) => {
    if (onTimeframeChange) {
      onTimeframeChange({ timeframe });
      return;
    }
    window.location.href = buildInsightsUrl(timeframe);
  };

  const applyCustomTimeframe = (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const customStartDate = formData.get("start_date");
    const customEndDate = formData.get("end_date");
    if (onTimeframeChange) {
      onTimeframeChange({
        timeframe: "custom",
        startDate: customStartDate,
        endDate: customEndDate,
      });
      return;
    }
    window.location.href = buildInsightsUrl(
      "custom",
      customStartDate,
      customEndDate
    );
  };

  const seeAllUrl = (() => {
    if (hideSeeAll || !website?.id) {
      return null;
    }
    const url = new URL(`/websites/${website.id}/cta`, window.location.origin);
    url.searchParams.set("list_type", "row");
    url.searchParams.set(
      "sort_by",
      topCampaigns?.sort_by || "conversion_score"
    );
    url.searchParams.set("timeframe", resolvedTimeframe);
    if (resolvedTimeframe === "custom") {
      if (startDate) {
        url.searchParams.set("start_date", startDate);
      }
      if (endDate) {
        url.searchParams.set("end_date", endDate);
      }
    }
    return url.toString();
  })();

  const campaignsInsightsUrl = buildInsightsUrl(
    resolvedTimeframe,
    startDate,
    endDate
  );

  useEffect(() => {
    if (window.location.hash !== `#${TOP_CAMPAIGNS_ANCHOR}`) {
      return;
    }

    const element = document.getElementById(TOP_CAMPAIGNS_ANCHOR);
    if (element) {
      element.scrollIntoView({ behavior: "auto", block: "start" });
    }
  }, []);

  return (
    <div className="WebsiteTopCampaigns">
      <div
        className="row hard-center"
        style={{
          marginLeft: 0,
          marginRight: 0,
          padding: "13px 15px 10px",
          borderBottom: "1px solid rgb(233, 235, 241)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "12px",
        }}
      >
        <div style={{ flex: "1 1 auto", minWidth: 0 }}>
          <h4 className="bold mb-0">
            {overview ? (
              <a href={campaignsInsightsUrl}>Top Campaigns</a>
            ) : (
              "Top Campaigns"
            )}
          </h4>
          <p
            className="insight-description light text-xl mb-0 mt-1"
            style={{
              display: "flex",
              alignItems: "center",
              position: "relative",
            }}
          >
            Sorted by conversion score
            <Tooltip
              tooltip="Unique viewers multiplied by conversion rate = Conversion score"
              inline={true}
            />
          </p>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            flexWrap: "nowrap",
            flexShrink: 0,
          }}
        >
          <form
            action={insightsUrl}
            method="get"
            style={{
              display: "inline-flex",
              alignItems: "center",
              margin: 0,
            }}
          >
            {!overview && (
              <input type="hidden" name="insight" value="campaigns" />
            )}
            <select
              name="timeframe"
              className="form-control !text-xl border-solid !border-b-[1px] !border-slate-200 !rounded-md !bg-transparent !shadow-none"
              value={selectedTimeframeState}
              onChange={(event) => {
                const nextTimeframe = event.target.value;
                setSelectedTimeframeState(nextTimeframe);
                if (nextTimeframe !== "custom") {
                  navigateToTimeframe(nextTimeframe);
                }
              }}
              style={{ width: "120px", height: "35px", flexShrink: 0 }}
            >
              {timeframeOptions.map((option) => (
                <option
                  key={`top-campaigns-timeframe-${option.value}`}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </select>
          </form>
          {exportTriggerUrl && (
            <ExportTriggerButton
              exportUrl={exportTriggerUrl}
              style={{ height: "35px", flexShrink: 0 }}
            />
          )}
          {seeAllUrl && (
            <a href={seeAllUrl} style={{ flexShrink: 0 }}>
              <button
                type="button"
                className="bg-white border-solid border-[1px] py-2 px-4 rounded-md border-slate-200 text-black text-xl hover:bg-blue-100 hover:border-blue-100 hover:text-blue-600"
                data-cta="inline"
                data-size="embed"
                style={{ height: "35px", whiteSpace: "nowrap" }}
              >
                See All →
              </button>
            </a>
          )}
        </div>
      </div>
      {selectedTimeframeState === "custom" && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "6px",
            padding: "0 15px 10px",
          }}
        >
          <form
            action={insightsUrl}
            method="get"
            onSubmit={applyCustomTimeframe}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              margin: 0,
            }}
          >
            {!overview && (
              <input type="hidden" name="insight" value="campaigns" />
            )}
            <input type="hidden" name="timeframe" value="custom" />
            <input
              type="date"
              name="start_date"
              defaultValue={startDate}
              className="form-control !text-xl !text-slate-500 border-solid !border-b-[1px] !border-slate-200 !rounded-md !bg-transparent !shadow-none"
              style={{ width: "110px", height: "35px" }}
            />
            <input
              type="date"
              name="end_date"
              defaultValue={endDate}
              className="form-control !text-xl !text-slate-500 border-solid !border-b-[1px] !border-slate-200 !rounded-md !bg-transparent !shadow-none"
              style={{ width: "110px", height: "35px" }}
            />
            <button
              type="submit"
              className="btn black-button"
              style={{ height: "35px" }}
            >
              Apply
            </button>
          </form>
        </div>
      )}

      {loadError ? (
        <div
          className="row hard-center"
          style={{ marginLeft: "20px", padding: "20px 10px" }}
        >
          <p className="text-xl light mb-0">
            Unable to load top campaigns.{" "}
            {onRetry && (
              <button
                type="button"
                className="btn-link"
                style={{
                  border: "none",
                  background: "none",
                  padding: 0,
                  color: "#2563eb",
                  cursor: "pointer",
                }}
                onClick={onRetry}
              >
                Retry
              </button>
            )}
          </p>
        </div>
      ) : loading ? (
        <div
          className="row hard-center"
          style={{ marginLeft: "20px", padding: "20px 10px" }}
        >
          <p className="text-xl light mb-0">Loading…</p>
        </div>
      ) : hasCampaigns ? (
        <div
          className="campaigns-metrics-scroll overflow-x-auto"
          style={{ scrollbarWidth: "thin" }}
        >
          <div
            className="row hard-center campaigns-metrics-table"
            style={{ marginLeft: "20px", paddingRight: "20px" }}
          >
            <ColumnTitles
              showSubmissions={showSubmissions === true}
              goals={goals}
            />
            {campaigns.map((campaign) => (
              <CampaignRow
                key={`top-campaign-${campaign.id}`}
                campaign={campaign}
                currency={currency}
                comparisonAvailable={comparisonAvailable}
                showWebsiteName={props.showWebsiteNames === true}
                showSubmissions={showSubmissions === true}
                goals={goals}
              />
            ))}
          </div>
        </div>
      ) : (
        <div
          className="row hard-center"
          style={{ marginLeft: "20px", overflow: "hidden" }}
        >
          <div
            className="not-a-link col-xs-12"
            style={{
              marginTop: "15px",
              marginLeft: "10px",
              paddingRight: "30px",
            }}
          >
            <a
              href={
                emptyStateUrl ||
                (website?.id ? `/websites/${website.id}/cta/new` : insightsUrl)
              }
            >
              <div className="not-a-link col-xs-12 some-vertical-space dark alert-blue semibold">
                <p className="not-a-link">
                  No campaigns created yet! Create one now{" "}
                  <span
                    className="glyphicon glyphicon-arrow-right"
                    style={{ fontSize: "11px !important" }}
                    aria-hidden="true"
                  />
                </p>
              </div>
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsiteTopCampaigns;
